import * as React from 'react'
import { View, Image, TouchableOpacity, Text, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import Tabbar from '@layout/Tabbar'
import Picker from '@components/picker/Picker'
import Icon from '@components/icon/Icons'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import { store } from '@utils/useSiteChangeHandler'

import useBreakpoint from '@utils/useBreakpoints'
import useTabs from '@utils/useTabs'
import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'

import Screens from '@layout/Screens'
import { VERSION_NAME } from '@app/config'

interface Props {
  children: JSX.Element | null | (JSX.Element | null)[]
}

const Drawer = ({ children }: Props) => {
  const [, bp] = useBreakpoint()
  const { menu } = useTabs()
  const i18n = useI18n()
  const navigation = useNavigation()

  const site = useReducer(SiteStore.store, (s) => s.site)
  const sites = useReducer(SiteStore.store, (s) => s.sites)
  const user = useReducer(UserStore.store, (s) => s.user)

  const status = useReducer(store, (s) => s.status)

  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const flatManifs = [...manifestations.incoming, ...manifestations.inProgress, ...manifestations.toBeClosed]

  const currentScreen = navigation.currentScreen()

  const siteOptions = React.useMemo(
    () => sites.map((s) => ({ label: i18n.t(`screens.site.sites.${s.codeLieu}`), value: s })),
    [sites]
  )
  const manifOptions = React.useMemo(() => flatManifs.map((m) => ({ label: m.libelle, value: m })), [flatManifs])

  if (bp === 'phone') {
    return (
      <TabContainer>
        {children}
        <Tabbar />
      </TabContainer>
    )
  }

  const onSiteSelected = (option?: PickerOption) => {
    if (option) {
      SiteStore.actions.setSite(option.value)
    }
  }

  const onManifestationSelected = (option?: PickerOption) => {
    if (option) {
      SiteStore.actions.setManifestation(option.value)
    }
  }

  const onLogoClicked = () => navigation.push('/')

  const renderItem = (item: ScreenName) => {
    const screen = Screens[item]
    const focused = currentScreen === item

    return (
      <Item key={item} focused={focused} activeOpacity={0.9} onPress={() => navigation.push(screen.path)}>
        <Icon size={25} name={screen.icon} color={Theme.colors.background} />
        <Name>{i18n.t(`screens.${item}.title`)}</Name>
      </Item>
    )
  }

  return (
    <LineContainer>
      <Container>
        <TouchableOpacity activeOpacity={0.9} onPress={onLogoClicked}>
          <Logo source={require('@assets/logo_viparis.png')} />
        </TouchableOpacity>
        <PickersContainer>
          {((user?.typeUtilisateur === 'ORGANISATEUR' && flatManifs.length > 1) || !!manifestation) && (
            <PickerContainer>
              <Picker
                value={manifestation ? manifOptions.find((m) => m.value.id === manifestation.id) : undefined}
                options={manifOptions}
                onChange={onManifestationSelected}
                disabled={status === 'loading'}
                placeholder={i18n.t('components.drawer.manifPlaceholder')}
              />
            </PickerContainer>
          )}
          {sites.length > 1 && (
            <Picker
              value={site ? siteOptions.find((s) => s.value.id === site.id) : undefined}
              options={siteOptions}
              onChange={onSiteSelected}
              disabled={status === 'loading'}
              placeholder={i18n.t('components.drawer.sitePlaceholder')}
            />
          )}
        </PickersContainer>

        <Links>{!!site && menu.map(renderItem)}</Links>

        {renderItem(Screens.account.name)}

        <VersionName>{i18n.t('common.version', { version: VERSION_NAME })}</VersionName>
      </Container>

      <ColumnContainer>{children}</ColumnContainer>
    </LineContainer>
  )
}

export default Drawer

const LineContainer = styled(View)`
  flex: 1;
  flex-direction: row;
`

const ColumnContainer = styled(View)`
  flex: 1;
  flex-direction: column;
`

const PickersContainer = styled(View)`
  flex-direction: column-reverse;
`

const PickerContainer = styled(View)`
  margin-top: 16px;
`

const Container = styled(View)`
  width: 250px;
  padding: 35px 15px;
  background-color: ${(props) => props.theme.colors.primary};
`

const TabContainer = styled(View)`
  flex: 1;
  flex-direction: column;
`

const Logo = styled(Image)`
  width: 150px;
  height: 40px;
  align-self: center;
  margin-bottom: 25px;
`

const Item = styled(TouchableOpacity)<{ focused: boolean }>`
  background-color: ${(props) => `${props.theme.colors.background}${props.focused ? '66' : '00'}`};
  padding: 12px 20px;
  flex-direction: row;
  align-items: center;
  border-radius: 6px;
  margin-top: 10px;
`

const Name = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.background};
  margin-left: 12px;
`

const Links = styled(ScrollView)`
  margin: 20px 0;
  flex: 1;
  z-index: -1;
`

const VersionName = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  text-align: center;
  color: ${(props) => props.theme.colors.backgroundTertiary};
  margin: 16px 0 -16px;
`
